/**
 * ============================================================================
 * TaskFlow ERP
 * Module : Campaign Management
 * Feature: Import Workspace
 *
 * File:
 * src/modules/campaign/prospectImport/components/importWorkspace/review/ReviewGrid.jsx
 *
 * Description:
 * Review Grid for Prospect Import.
 *
 * Responsibilities
 * ----------------
 * • Search Imported Records
 * • Filter by Status
 * • Display Imported Records
 * • Row Selection
 * • Launch Compare/Edit/View
 * • Import Records
 *
 * No business logic.
 * No API calls.
 * ============================================================================
 */

import React from "react";

import {
    Card,
    Row,
    Col,
    Form,
    Table,
    Button,
    Badge,
    InputGroup
} from "react-bootstrap";

export default function ReviewGrid({

    config,

    state,

    actions

}) {

    // ==========================================================
    // STATE
    // ==========================================================

    const {

        records = [],

        selectedRecord,

        searchText = "",

        statusFilter = "ALL",

        canExecuteImport = false

    } = state;

    // ==========================================================
    // ACTIONS
    // ==========================================================

    const {

        updateSearch,

        updateStatusFilter,

        selectRecord,

        compare,

        edit,

        view,

        importRecords,

        reset

    } = actions;

    // ==========================================================
    // STATUS BADGE
    // ==========================================================

    const getStatusBadge = (status) => {

        switch ((status || "").toUpperCase()) {

            case "VERIFIED":

                return (
                    <Badge bg="success">

                        Verified

                    </Badge>
                );

            case "DUPLICATE":

                return (
                    <Badge bg="warning" text="dark">

                        Duplicate

                    </Badge>
                );

            case "WARNING":

                return (
                    <Badge bg="info">

                        Warning

                    </Badge>
                );

            case "INVALID":

                return (
                    <Badge bg="danger">

                        Invalid

                    </Badge>
                );

            default:

                return (

                    <Badge bg="secondary">

                        Unknown

                    </Badge>

                );

        }

    };

    return (

        <Card className="shadow-sm mt-4">

            {/* ====================================================== */}
            {/* HEADER */}
            {/* ====================================================== */}

            <Card.Header>

                <Row className="align-items-center">

                    <Col>

                        <h5 className="mb-0">

                            Review Imported Records

                        </h5>

                    </Col>

                    <Col xs="auto">

                        <Button
                            variant="outline-secondary"
                            size="sm"
                        >

                            <i className="fas fa-file-export me-2"></i>

                            Export

                        </Button>

                    </Col>

                </Row>

            </Card.Header>

            <Card.Body>

                {/* ================================================== */}
                {/* SEARCH & FILTER */}
                {/* ================================================== */}

                <Row className="mb-4">

                    <Col lg={5}>

                        <InputGroup>

                            <InputGroup.Text>

                                <i className="fas fa-search"></i>

                            </InputGroup.Text>

                            <Form.Control

                                placeholder="Search Prospect..."

                                value={searchText}

                                onChange={(e) =>

                                    updateSearch(
                                        e.target.value
                                    )

                                }

                            />

                        </InputGroup>

                    </Col>

                    <Col lg={3}>

                        <Form.Select

                            value={statusFilter}

                            onChange={(e) =>

                                updateStatusFilter(
                                    e.target.value
                                )

                            }

                        >

                            <option value="ALL">

                                All Status

                            </option>

                            <option value="VERIFIED">

                                Verified

                            </option>

                            <option value="WARNING">

                                Warning

                            </option>

                            <option value="DUPLICATE">

                                Duplicate

                            </option>

                            <option value="INVALID">

                                Invalid

                            </option>

                        </Form.Select>

                    </Col>

                    <Col
                        lg={2}
                        className="text-lg-end mt-3 mt-lg-0"
                    >

                        <small className="text-muted">

                            Records

                        </small>

                        <div>

                            <strong>

                                {records.length}

                            </strong>

                        </div>

                    </Col>

                </Row>
                                {/* ================================================== */}
                {/* REVIEW GRID */}
                {/* ================================================== */}

                <Table

                    bordered

                    hover

                    responsive

                    className="align-middle"

                >

                    <thead className="table-light">

                        <tr>

                            <th width="50">

                                Select

                            </th>

                            <th width="80">

                                Row

                            </th>

                            <th>

                                Prospect Name

                            </th>

                            <th width="120">

                                Contacts

                            </th>

                            <th width="150">

                                External ID

                            </th>

                            <th width="140">

                                Status

                            </th>

                            <th>

                                Remarks

                            </th>

                        </tr>

                    </thead>

                    <tbody>

                        {

                            records.length === 0 && (

                                <tr>

                                    <td

                                        colSpan="7"

                                        className="text-center py-5"

                                    >

                                        <i
                                            className="fas fa-folder-open fa-2x text-muted mb-3"
                                        ></i>

                                        <div>

                                            No records available.

                                        </div>

                                    </td>

                                </tr>

                            )

                        }

                        {

                            records.map((record) => (

                                <tr

                                    key={record.id}

                                    className={

                                        selectedRecord?.id === record.id

                                            ? "table-active"

                                            : ""

                                    }

                                >

                                    <td className="text-center">

                                        <Form.Check

                                            type="radio"

                                            checked={

                                                selectedRecord?.id === record.id

                                            }

                                            onChange={() =>

                                                selectRecord(record.id)

                                            }

                                        />

                                    </td>

                                    <td>

                                        {record.row_no}

                                    </td>

                                    <td>

                                        <strong>

                                            {record.name}

                                        </strong>

                                    </td>

                                    <td>

                                        {record.contacts}

                                    </td>

                                    <td>

                                        {record.external_id}

                                    </td>

                                    <td>

                                        {

                                            getStatusBadge(

                                                record.status

                                            )

                                        }

                                    </td>

                                    <td>

                                        {

                                            record.remarks ||

                                            "-"

                                        }

                                    </td>

                                </tr>

                            ))

                        }

                    </tbody>

                </Table>
                                {/* ================================================== */}
                {/* FOOTER */}
                {/* ================================================== */}

                <Row className="align-items-center mt-3">

                    <Col>

                        <small className="text-muted">

                            Showing

                            <strong className="mx-1">

                                {records.length}

                            </strong>

                            record(s)

                        </small>

                    </Col>

                    <Col xs="auto">

                        <small className="text-muted">

                            Selected :

                            <strong className="ms-1">

                                {selectedRecord ? 1 : 0}

                            </strong>

                        </small>

                    </Col>

                </Row>

                <hr />

                {/* ================================================== */}
                {/* ACTION BUTTONS */}
                {/* ================================================== */}

                <Row className="mt-3">

                    <Col>

                        <Button

                            variant="outline-primary"

                            className="me-2"

                            disabled={!selectedRecord}

                            onClick={compare}

                        >

                            <i className="fas fa-columns me-2"></i>

                            Compare

                        </Button>

                        <Button

                            variant="outline-warning"

                            className="me-2"

                            disabled={!selectedRecord}

                            onClick={edit}

                        >

                            <i className="fas fa-edit me-2"></i>

                            Edit

                        </Button>

                        <Button

                            variant="outline-info"

                            className="me-2"

                            disabled={!selectedRecord}

                            onClick={view}

                        >

                            <i className="fas fa-eye me-2"></i>

                            View

                        </Button>

                    </Col>

                    <Col
                        xs="auto"
                    >

                        <Button

                            variant="success"

                            className="me-2"

                            disabled={!canExecuteImport}

                            onClick={importRecords}

                        >

                            <i className="fas fa-file-import me-2"></i>

                            Import

                        </Button>

                        <Button

                            variant="secondary"

                            onClick={reset}

                        >

                            <i className="fas fa-times me-2"></i>

                            Close

                        </Button>

                    </Col>

                </Row>
                            </Card.Body>

        </Card>

    );

}